import { ORPCError } from '@orpc/client'
import { type AppError, appError, isAppError } from '../AppError'
import { getErrorMeta } from '../codes'

/**
 * Convert any error to an ORPCError (client-side flavor).
 * Intended for transport boundaries; in client apps usually not needed,
 * but provided for completeness and testing.
 */
export function toORPCError(err: unknown): ORPCError<string, unknown> {
  if (isAppError(err)) {
    const meta = getErrorMeta(err.code)
    return new ORPCError(err.code, {
      status: err.status ?? meta?.defaultStatus ?? 500,
      message: err.message,
      data: err.data,
      cause: (err as Error).cause,
    })
  }
  if (err instanceof ORPCError) {
    // Re-wrap to ensure a consistent generic type and avoid leaking unknown types
    return new ORPCError<string, unknown>(err.code as string, {
      status: err.status,
      message: err.message,
      data: err.data as unknown,
      cause: (err as Error).cause,
    })
  }
  return new ORPCError<string, unknown>('INTERNAL', {
    status: 500,
    message: 'Converted from unknown error',
    data: { originalType: typeof err },
  })
}

/**
 * Convert an ORPCError into an AppError for internal handling and UI mapping.
 */
export function fromORPCError(err: unknown): AppError {
  if (err instanceof ORPCError) {
    return appError(err.code as never, {
      message: err.message,
      status: err.status,
      data: err.data as unknown,
      cause: (err as Error).cause,
    })
  }
  return appError('INTERNAL', { status: 500, cause: err })
}
